import React, { PropsWithChildren, useEffect, useState } from 'react';

import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Box, SxProps, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import { GridRowSelectionModel } from '@mui/x-data-grid-pro';

import useTableContext from '../../../admin-portal/BaseTabsRouting/Headers/HeaderGrid/useTableContext';
import CreateButton from '../../CreateButton/CreateButton';
import GridHeader from './styled/GridHeader';
import { AdditionalButton, CustomDataGridProps } from '../DataGrid.types';

type Props = {
  rowSelectionModel: GridRowSelectionModel;
  setShowDeleteConfirmation: React.Dispatch<boolean>;
  setDeleteSelection: React.Dispatch<boolean>;
  onAddClickTitle?: string;
  additionalButtons?: AdditionalButton[];
  onAddClick?: () => void;
  onDeleteClick?: CustomDataGridProps['onDeleteClick'];
  disableActions?: boolean;
  checkboxSelection?: boolean;
  title?: string | React.ReactNode;
  headerContent?: React.ReactNode;
  sx?: SxProps;
};

const addAdditionalButtons = (additionalButtons: AdditionalButton[]) => {
  return additionalButtons?.map((additionalButton) => (
    <Button
      startIcon={additionalButton.startIcon ?? null}
      key={additionalButton.label}
      variant="contained"
      onClick={additionalButton.onClick}
      disabled={additionalButton.disabled || false}
      color={additionalButton.color}
    >
      {additionalButton.label}
    </Button>
  ));
};

const DataGridHeader = ({
  onAddClickTitle,
  additionalButtons,
  onAddClick,
  onDeleteClick,
  title,
  rowSelectionModel,
  setShowDeleteConfirmation,
  setDeleteSelection,
  disableActions,
  checkboxSelection,
  headerContent,
  sx
}: Props) => {
  const { t } = useTranslation('common');

  return (
    <GridHeader sx={sx}>
      <Box sx={{ display: 'flex' }}>
        {checkboxSelection && onDeleteClick && !disableActions && (
          <Button
            variant="contained"
            onClick={() => {
              setDeleteSelection(true);
              setShowDeleteConfirmation(true);
            }}
            disabled={rowSelectionModel.ids.size === 0}
          >
            {t('deleteSelection')}
          </Button>
        )}
        {title && !checkboxSelection && (
          <Typography variant="h6">{title}</Typography>
        )}
        {headerContent}
      </Box>
      <Box ml="auto" display="flex" gap={1}>
        {onAddClick && !disableActions && (
          <CreateButton onClick={onAddClick} label={onAddClickTitle} />
        )}
        {!disableActions &&
          additionalButtons &&
          addAdditionalButtons(additionalButtons)}
      </Box>
    </GridHeader>
  );
};

export default DataGridHeader;

export const DataGridHeaderPortal: React.FC<PropsWithChildren<object>> = (
  props
) => {
  const { contentNodeForButtons } = useTableContext();
  const [loaded, setLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (contentNodeForButtons?.current) setLoaded(true);
  }, [contentNodeForButtons?.current]);

  return loaded ? (
    createPortal(
      <>{props.children}</>,
      contentNodeForButtons.current as HTMLDivElement
    )
  ) : (
    <></>
  );
};
